// ui/services/aiService.ts
// 目的：把前端「AI 功能鍵」從占位改成真正呼叫 Pages Functions（同網域，避免 CORS）
//
// ✅ /api/ai-analyze-document  (multipart/form-data, 回 JSON：summary/fields/suggestReply...)
// ✅ /api/ai-assistant        (json, 回 { reply }, 支援 downloadUrl 附件)
//
// 若後端尚未設定 OPENAI_API_KEY，或連線失敗：會回傳 ok:false，並附上 message 供 UI 顯示。

import { extractPlainTextFromFile } from "../utils/fileTextExtractor";
import { saveAiAnalysisLog } from "./aiAuditService";

export type AIAnalysisMode =
  | "summary"
  | "analysis"
  | "draft"
  | "impact"
  | "extract"
  | "risk"
  | "extractFields"
  | "contract_extraction"
  | "version_compare"
  | "batch_analysis"
  | "general";

export type AttachmentLike = {
  name: string;
  mimeType?: string;
  downloadUrl?: string;

  // 允許帶其他不固定欄位（避免 TS 太嚴格卡住）
  [k: string]: any;
};

export type AnalyzeUploadedFileInput = {
  // 常見：先上傳到 storage，再丟 url / meta 給 AI
  url?: string;
  fileMeta?: AttachmentLike;

  // 有些頁面直接傳 File 或 files
  file?: File;
  files?: File[];

  // 補充資訊
  mode?: AIAnalysisMode;
  purpose?: string;
  documentType?: string;
  contractId?: string;
  context?: string;
  extraContext?: any;
  userInstruction?: string;
  prompt?: string;

  // 允許帶其他不固定欄位（避免 TS 太嚴格卡住）
  [k: string]: any;
};

export type AnalyzeInputLike = AnalyzeUploadedFileInput | File;

export type AnalyzeResult = {
  ok: true;
  summary: string;
  title?: string;
  description?: string;
  counterparty?: string;

  // 兼容部分頁面可能會用到的欄位
  name?: string;
  type?: string;
  size?: number;

  // 後端可能回傳的結構化結果
  fields?: any;
  keyRisks?: any[];
  todoFields?: string[];
  suggestReply?: string;

  // 一些頁面用的別名（避免到處改 UI）
  riskFlags?: string[];
  draft?: string;

  // 風險評估（部分頁面直接顯示/回填）
  riskAssessmentText?: string;

  // debug / 備用
  text?: string;
  raw?: any;
  meta?: any;
};

export type CallLegalAssistantInput =
  | string
  | {
      mode?: string;
      contextType?: string;
      contextId?: string;
      userInstruction?: string;
      notes?: string;
      attachments?: AttachmentLike[];
      currentFields?: any;
      templateText?: string;
      [k: string]: any;
    };

export type CallLegalAssistantResponse = {
  ok: boolean;
  text: string;
  message?: string;
  raw?: any;
};

function safeText(v: any) {
  return typeof v === "string" ? v.trim() : "";
}

function stripExt(name: string) {
  const idx = name.lastIndexOf(".");
  if (idx <= 0) return name;
  return name.slice(0, idx);
}

function isLikelyContractExtraction(mode?: any) {
  const m = String(mode || "").toLowerCase();
  return (
    m.includes("extract") ||
    m.includes("autofill") ||
    m.includes("fill") ||
    m.includes("contract_extraction") ||
    m.includes("extractfields")
  );
}

function coerceFiles(input: AnalyzeInputLike): File[] {
  if (!input) return [];
  // Some callers pass the raw files array directly.
  if (Array.isArray(input)) {
    return input.filter((f: any) => typeof File !== "undefined" && f instanceof File);
  }
  if (typeof File !== "undefined" && input instanceof File) return [input];
  const obj: any = input as any;
  if (obj?.file && typeof File !== "undefined" && obj.file instanceof File) return [obj.file];
  if (Array.isArray(obj?.files)) return obj.files.filter((f: any) => typeof File !== "undefined" && f instanceof File);
  // 有些地方可能會把 File 塞在 fileMeta.file
  if (obj?.fileMeta?.file && typeof File !== "undefined" && obj.fileMeta.file instanceof File) return [obj.fileMeta.file];
  // ContractRepoForm 會傳 rawFiles
  if (Array.isArray(obj?.rawFiles)) return obj.rawFiles.filter((f: any) => typeof File !== "undefined" && f instanceof File);
  return [];
}

function jsonStringifySafe(v: any, maxLen = 5000): string {
  try {
    const s = typeof v === "string" ? v : JSON.stringify(v, null, 2);
    if (!s) return "";
    return s.length > maxLen ? s.slice(0, maxLen) : s;
  } catch {
    return "";
  }
}

function pickFieldsStr(inputObj: any): string {
  // 盡量不猜 UI 結構，先從常見欄位拿
  const candidate =
    inputObj?.currentFields ??
    inputObj?.fields ??
    inputObj?.currentForm ??
    inputObj?.form ??
    inputObj?.formState ??
    inputObj?.payload ??
    inputObj?.data;

  return jsonStringifySafe(candidate, 5000);
}

function pickTemplateStr(inputObj: any): string {
  const candidate =
    inputObj?.templateText ??
    inputObj?.template ??
    inputObj?.clauseTemplate ??
    inputObj?.contractTemplate ??
    inputObj?.templateContent;
  return safeText(candidate) ? jsonStringifySafe(candidate, 5000) : "";
}

async function parseJsonSafe(res: Response): Promise<any> {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { rawText: text };
  }
}

function toRiskFlags(keyRisks: any[]): string[] {
  if (!Array.isArray(keyRisks)) return [];
  return keyRisks
    .map((r) => safeText(r?.risk || r?.title || r?.name || r))
    .filter(Boolean)
    .slice(0, 20);
}


function buildRiskAssessmentText(data: any, riskFlags?: string[]) {
  const sf = data?.sfRiskBenefit;
  const keyRisks = Array.isArray(data?.keyRisks) ? data.keyRisks : [];
  const hasHigh = keyRisks.some((r: any) => safeText(r?.severity).toLowerCase() === "high");
  const hasMed = keyRisks.some((r: any) => safeText(r?.severity).toLowerCase() === "medium");

  const levelFromSf = safeText(sf?.riskLevel);
  const levelFromRisks = hasHigh ? "高" : hasMed ? "中" : "低";
  const level = levelFromSf || levelFromRisks;

  const reasons = Array.isArray(sf?.reasons) ? sf.reasons.map((x: any) => safeText(x)).filter(Boolean) : [];
  const actions = Array.isArray(sf?.actionItems) ? sf.actionItems.map((x: any) => safeText(x)).filter(Boolean) : [];
  const owner = safeText(sf?.owner) || "";

  const lines: string[] = [];
  lines.push("【風險評估】");
  if (level) lines.push(`- 等級：${level}`);
  if (owner) lines.push(`- 主責：${owner}`);
  if (reasons.length) lines.push(`- 主要原因：${reasons.slice(0, 4).join("；")}`);
  if (actions.length) lines.push(`- 建議動作：${actions.slice(0, 4).join("；")}`);

  // 若 sfRiskBenefit 不完整，仍補一條風險聚合提示
  if (!reasons.length && (riskFlags?.length || keyRisks.length)) {
    const topRisks = keyRisks.slice(0, 4).map((r: any) => safeText(r?.risk || r?.title || r)).filter(Boolean);
    const topFlags = (riskFlags || []).slice(0, 4);
    const hint = [...topRisks, ...topFlags].filter(Boolean).join("；");
    if (hint) lines.push(`- 提示：${hint}`);
  }

  return lines.join("\n");
}

function mapDefaultAnalysisToResult(fileNameLike: { name: string; type?: string; size?: number }, data: any): AnalyzeResult {
  const summary = safeText(data?.summary) || "（無法產生摘要）";
  const title =
    safeText(data?.fields?.title) || safeText(data?.fields?.subject) || stripExt(fileNameLike.name) || "文件";
  const suggestReply = safeText(data?.suggestReply) || "";
  const keyRisks = Array.isArray(data?.keyRisks) ? data.keyRisks : [];
  const riskFlags = toRiskFlags(keyRisks);

  const rawFields = data?.fields || {};
  const fieldsV1 = normalizeFieldsSchemaV1({
    title,
    mainDate: rawFields?.mainDate,
    summary,
    amount: rawFields?.amount,
    currency: rawFields?.currency,
    counterparty: rawFields?.counterparty || rawFields?.counterpartyName,
    category: rawFields?.category,
    extra: rawFields,
  });

  const riskAssessmentText = buildRiskAssessmentText(data, riskFlags);

  return {
    ok: true,
    summary,
    title,
    description: suggestReply || summary,
    counterparty: safeText(data?.fields?.counterparty) || safeText(data?.fields?.counterpartyName) || "",
    name: fileNameLike.name,
    type: fileNameLike.type,
    size: fileNameLike.size,
    fields: fieldsV1,
    keyRisks,
    riskFlags,
    todoFields: Array.isArray(data?.todoFields) ? data.todoFields : [],
    suggestReply,
    draft: suggestReply, // 兼容某些頁面用 draft 顯示
    riskAssessmentText,
    raw: data,
    meta: { api: "ai-analyze-document", mode: "analysis" },
  };
}

function mapContractExtractionToResult(fileNameLike: { name: string; type?: string; size?: number }, data: any): AnalyzeResult {
  const summary = safeText(data?.summary) || "（無法產生摘要）";
  const title = safeText(data?.contractTitle) || stripExt(fileNameLike.name) || "合約";

  const totalAmount = data?.totalAmount || {};
  const currency = safeText(totalAmount?.currency) || "";
  const amountTextRaw =
    safeText(totalAmount?.text) ||
    safeText(data?.amount) ||
    (totalAmount?.value != null ? String(totalAmount.value) : "");

  // contract_extraction 後端可能回 risks(含 severity) 或 riskSummary(字串陣列)
  const risksArr = Array.isArray(data?.risks) ? data.risks : [];
  const riskSummaryArr = Array.isArray(data?.riskSummary) ? data.riskSummary : [];
  const keyRisks = risksArr.length
    ? risksArr
    : riskSummaryArr
        .map((r: any) => ({ risk: safeText(r), severity: "medium" }))
        .filter((r: any) => Boolean(r.risk));
  const riskFlags = toRiskFlags(keyRisks);

  const compatFields = {
    title,
    effectiveDate: safeText(data?.effectiveDate) || safeText(data?.term?.periodStart) || "",
    expiryDate: safeText(data?.term?.periodEnd) || "",
    amount: amountTextRaw,
    currency,
    partyA: safeText(data?.parties?.partyA) || "",
    partyB: safeText(data?.parties?.partyB) || safeText(data?.counterpartyName) || "",
  };

  const fieldsRich = {
    ...compatFields,
    contractTitle: data?.contractTitle,
    counterpartyName: data?.counterpartyName,
    totalAmount,
    term: data?.term,
    obligations: data?.obligations,
    risks: risksArr.length ? risksArr : keyRisks,
    riskSummary: riskSummaryArr,
  };

  const fieldsV1 = normalizeFieldsSchemaV1({
    title,
    mainDate: safeText(data?.term?.signDate) || compatFields.effectiveDate || "",
    summary,
    amount: amountTextRaw,
    currency,
    counterparty: compatFields.partyB,
    category: "CONTRACT",
    extra: fieldsRich,
  });

  const riskAssessmentText = buildRiskAssessmentText({ ...(data || {}), keyRisks }, riskFlags);

  // description：給列表/卡片使用
  const descParts: string[] = [];
  const amt = totalAmount?.value != null ? `${totalAmount.value} ${currency || ""}`.trim() : amountTextRaw;
  if (amt) descParts.push(`合約總價額：${amt}`);
  const signDate = safeText(data?.term?.signDate);
  if (signDate) descParts.push(`簽約日：${signDate}`);
  const ps = safeText(data?.term?.periodStart);
  if (ps) descParts.push(`起日：${ps}`);
  const pe = safeText(data?.term?.periodEnd);
  if (pe) descParts.push(`迄日：${pe}`);

  const description = descParts.join("｜");

  return {
    ok: true,
    summary,
    title,
    description,
    counterparty: compatFields.partyB,
    name: fileNameLike.name,
    type: fileNameLike.type,
    size: fileNameLike.size,
    fields: fieldsV1,
    keyRisks,
    riskFlags,
    todoFields: Array.isArray(data?.todoChecklist) ? data.todoChecklist : [],
    suggestReply: "",
    draft: "",
    riskAssessmentText,
    raw: data,
    meta: { api: "ai-analyze-document", mode: "contract_extraction" },
  };
}

function buildAssistantAnalyzePrompt(args: {
  contextMode: "analysis" | "contract_extraction";
  fieldsStr?: string;
  templateStr?: string;
  userInstruction?: string;
}) {
  const { contextMode, fieldsStr, templateStr, userInstruction } = args;

  if (contextMode === "contract_extraction") {
    return `你是一位台灣公司法務助理。請閱讀我提供的附件檔案內容，並「只輸出 JSON」（不要任何解釋或前後文）。

【輸出 JSON schema】
{
  "contractTitle": "string",
  "counterpartyName": "string",
  "summary": "string",
  "totalAmount": { "currency": "TWD|USD|JPY|KRW|OTHER", "value": number|string, "note": "string" },
  "term": { "signDate": "YYYY-MM-DD|string", "periodStart": "YYYY-MM-DD|string", "periodEnd": "YYYY-MM-DD|string", "note": "string" },
  "obligations": [ { "party": "A|B|Both|Unknown", "duty": "string", "due": "string", "evidence": "string" } ],
  "risks": [ { "risk": "string", "severity": "low|medium|high", "reason": "string", "suggestion": "string" } ]
}

【目前表單欄位（可能為空）】
${fieldsStr || "{}"}

【額外指示】
${userInstruction || "請盡量從文件中抽取可回填的合約總價額、簽約日/起迄日、並列出風險與義務。"}

【注意】
- 無法辨識的欄位請留空字串或合理 note
- currency 若不確定用 OTHER 並在 note 說明`;
  }

  return `你是一位台灣公司法務助理。請閱讀我提供的附件檔案內容，並「只輸出 JSON」（不要任何解釋或前後文）。

【輸出 JSON schema】
{
  "summary": "string",
  "fields": {
    "title": "string",
    "counterparty": "string",
    "amount": "string|number",
    "currency": "TWD|USD|JPY|KRW|OTHER|string",
    "mainDate": "YYYY-MM-DD|string",
    "category": "string"
  },
  "keyRisks": [ { "risk": "string", "severity": "low|medium|high", "reason": "string", "suggestion": "string" } ],
  "todoFields": [ "string" ],
  "suggestReply": "string"
}

【目前表單欄位（可能為空）】
${fieldsStr || "{}"}

【模板/格式偏好（若有）】
${templateStr || ""}

【額外指示】
${userInstruction || "請先給摘要，再列出風險與待辦，最後提供可直接貼上的草稿（suggestReply）。"}

【注意】
- 如果文件是函/公文：suggestReply 以「函覆草稿」風格
- 如果文件是合約：summary 請包含重點條款/付款/期限/責任`;
}

async function analyzeViaAssistant(args: {
  url: string;
  name: string;
  mimeType?: string;
  contextMode: "analysis" | "contract_extraction";
  fieldsStr?: string;
  templateStr?: string;
  userInstruction?: string;
}) {
  const promptText = buildAssistantAnalyzePrompt({
    contextMode: args.contextMode,
    fieldsStr: args.fieldsStr,
    templateStr: args.templateStr,
    userInstruction: args.userInstruction,
  });

  const payload = {
    mode: "analyze_document",
    promptText,
    attachments: [
      {
        name: args.name,
        mimeType: args.mimeType,
        downloadUrl: args.url,
      },
    ],
  };

  const res = await fetch("/api/ai-assistant", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const msg = safeText(data?.message) || safeText(data?.error) || `AI 回覆失敗（HTTP ${res.status}）`;
    return { ok: false as const, message: msg, raw: data };
  }

  const reply = safeText(data?.reply) || "";
  if (!reply) return { ok: false as const, message: "AI 未回傳內容", raw: data };

  // 嘗試把 reply 當 JSON parse
  try {
    const parsed = JSON.parse(reply);
    return { ok: true as const, parsed, raw: data };
  } catch {
    // 回傳非 JSON：至少把它當作摘要
    return { ok: true as const, parsed: { summary: reply }, raw: data };
  }
}


async function analyzeViaAssistantWithExtractedText(args: {
  promptText: string;
  name: string;
  mimeType?: string;
  extractedText: string;
}) {
  const payload = {
    mode: "analyze_document",
    promptText: args.promptText,
    attachments: [
      {
        name: args.name,
        mimeType: args.mimeType,
        // downloadUrl 可留空（有 extractedText 就不會 fetch）
        downloadUrl: "",
        extractedText: args.extractedText,
      },
    ],
  };

  const res = await fetch("/api/ai-assistant", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const msg =
      safeText(data?.message) || safeText(data?.error) || `AI 回覆失敗（HTTP ${res.status}）`;
    return { ok: false as const, message: msg, raw: data };
  }

  const reply = safeText(data?.reply) || "";
  if (!reply) return { ok: false as const, message: "AI 未回傳內容", raw: data };

  try {
    const parsed = JSON.parse(reply);
    return { ok: true as const, parsed, raw: data };
  } catch {
    return { ok: true as const, parsed: { summary: reply }, raw: data };
  }
}

function buildCustomJsonPrompt(args: {
  documentType: string;
  fieldsStr?: string;
  templateStr?: string;
  userInstruction?: string;
}) {
  const { documentType, fieldsStr, templateStr, userInstruction } = args;

  if (documentType === "seal_application") {
    return `你是一位台灣公司法務助理。請閱讀我提供的附件內容，並「只輸出 JSON」（不要任何解釋或前後文）。

【輸出 JSON schema】
{
  "title": "string",
  "mainDate": "YYYY-MM-DD|string",
  "amount": "string|number",
  "currency": "TWD|USD|JPY|KRW|EUR|OTHER|UNKNOWN|string",
  "summary": "string",
  "sealType": "COMPANY_BOTH_SEALS|COMPANY_ONLY|REPRESENTATIVE_ONLY|OTHER",
  "docCategory": "CONTRACT|NOTICE|LITIGATION|GOVERNANCE|OTHER",
  "copyCount": 1,
  "notes": "string"
}

【目前表單欄位（可能為空）】
${fieldsStr || "{}"}

【模板/格式偏好（若有）】
${templateStr || ""}

【額外指示】
${userInstruction || "請優先從附件內容抽取可回填資訊；無法判斷的欄位請填 OTHER/UNKNOWN 或留空字串。"}

【注意】
- copyCount 若文件未載明，預設 1
- amount 若有多個金額，請挑『本案主要金額』，其餘放 notes`;
  }

  if (documentType === "brand_asset" || documentType === "ip_asset") {
    const isBrand = documentType === "brand_asset";
    return `你是一位台灣公司法務助理。請閱讀我提供的附件內容，並「只輸出 JSON」（不要任何解釋或前後文）。

【輸出 JSON schema】
{
  "name": "string", 
  "type": "${isBrand ? "商標|著作權|專利|授權|其他" : "專利|商標|著作權|技術文件|其他"}",
  "region": "string",
  "expiryDate": "YYYY-MM-DD|string",
  "summary": "string",
  "riskNotes": "string",
  "title": "string",
  "mainDate": "YYYY-MM-DD|string",
  "amount": "string|number",
  "currency": "TWD|USD|JPY|KRW|EUR|OTHER|UNKNOWN|string"
}

【目前表單欄位（可能為空）】
${fieldsStr || "{}"}

【模板/格式偏好（若有）】
${templateStr || ""}

【額外指示】
${userInstruction || "請從附件中抽取 name/type/region/expiryDate 及摘要；若未見到期日請寫未見或留空。"}

【注意】
- 若文件完全看不出 name/type，請以檔名推測並在 riskNotes 說明是推測
- expiryDate 若有多個日期，請優先選『權利/授權到期日』`;
  }

  // fallback
  return buildAssistantAnalyzePrompt({
    contextMode: "analysis",
    fieldsStr,
    templateStr,
    userInstruction,
  });
}

function parseAmountText(input: any): { amountText: string; amountValue: number | null; amountCurrency: string } {
  const raw = typeof input === "number" ? String(input) : safeText(input);
  const s = raw.replace(/\s+/g, " ").trim();
  if (!s) return { amountText: "", amountValue: null, amountCurrency: "" };

  const currency =
    /NT\$|TWD|新台幣|台幣|NTD/i.test(s)
      ? "TWD"
      : /US\$|USD|美金/i.test(s)
      ? "USD"
      : /JPY|日圓|円/i.test(s)
      ? "JPY"
      : /EUR|歐元/i.test(s)
      ? "EUR"
      : /KRW|韓元/i.test(s)
      ? "KRW"
      : "";

  // 抓第一個看起來像數字的片段
  const m = s.match(/([0-9]{1,3}(?:,[0-9]{3})+|[0-9]+)(?:\.[0-9]+)?/);
  if (!m) return { amountText: s, amountValue: null, amountCurrency: currency };
  const numStr = m[0].replace(/,/g, "");
  const val = Number(numStr);
  return { amountText: s, amountValue: Number.isFinite(val) ? val : null, amountCurrency: currency };
}

function normalizeFieldsSchemaV1(args: {
  title?: string;
  mainDate?: string;
  summary?: string;
  amount?: any;
  currency?: any;
  counterparty?: string;
  category?: string;
  extra?: Record<string, any>;
}) {
  const { title, mainDate, summary, amount, currency, counterparty, category, extra } = args;
  const parsed = parseAmountText(amount);
  const amountCurrency = safeText(currency) || parsed.amountCurrency;

  // NOTE: extra 可能帶入同名 key（例如 amountValue 是字串），
  // 因此我們先 spread extra，再用 schema v1 的「計算後欄位」覆蓋回來，
  // 以確保 amountValue/amountCurrency 等可回填欄位型別穩定。
  const extraObj = (extra || {}) as any;

  const fallbackTitle = safeText(extraObj?.title || extraObj?.name);
  const fallbackDate = safeText(extraObj?.mainDate || extraObj?.date || extraObj?.signedDate || extraObj?.effectiveDate);
  const fallbackSummary = safeText(extraObj?.summary || extraObj?.abstract);
  const fallbackCounterparty = safeText(extraObj?.counterparty || extraObj?.partyB || extraObj?.vendor || extraObj?.customer);
  const fallbackCategory = safeText(extraObj?.category || extraObj?.docType);

  // amount fallback：允許 extra 直接給數字/字串
  const rawAmountFallback = extraObj?.amount ?? extraObj?.totalAmount ?? extraObj?.amountText ?? extraObj?.amountValue;
  const parsedFallback = parsed.amountText ? parsed : parseAmountText(rawAmountFallback);
  const amountCurrency2 = amountCurrency || parsedFallback.amountCurrency || safeText(extraObj?.currency);
  const amountValue2 =
    parsedFallback.amountValue != null
      ? parsedFallback.amountValue
      : (() => {
          const n = Number(String(extraObj?.amountValue || "").replace(/,/g, ""));
          return Number.isFinite(n) ? n : null;
        })();

  return {
    schemaVersion: 1,
    ...(extraObj || {}),

    // --- 統一可回填欄位（優先：顯式 args，其次：extra fallback） ---
    title: safeText(title) || fallbackTitle,
    mainDate: safeText(mainDate) || fallbackDate,
    summary: safeText(summary) || fallbackSummary,
    counterparty: safeText(counterparty) || fallbackCounterparty,
    category: safeText(category) || fallbackCategory,

    // --- 金額（統一欄位：amountText/amountValue/amountCurrency） ---
    amount: safeText(amount) || safeText(rawAmountFallback) || (amountValue2 != null ? String(amountValue2) : ""),
    currency: amountCurrency2,
    amountText: parsedFallback.amountText,
    amountValue: amountValue2,
    amountCurrency: amountCurrency2,

    // 方便部分模組使用的別名
    date: safeText(mainDate) || fallbackDate,
  };
}

async function fetchMetaAsFile(meta: any): Promise<File | null> {
  try {
    if (!meta) return null;
    const url = safeText(meta?.url || meta?.downloadUrl || meta?.downloadURL);
    if (!url) return null;

    const name = safeText(meta?.name || meta?.fileName || meta?.filename || "attachment");
    const hintedType = safeText(meta?.contentType || meta?.mimeType || meta?.type);

    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const finalType = hintedType || safeText((blob as any)?.type);
    return new File([blob], name, finalType ? { type: finalType } : undefined);
  } catch {
    return null;
  }
}

export async function analyzeUploadedFile(
  input: AnalyzeInputLike
): Promise<AnalyzeResult | { ok: false; message: string; raw?: any }> {
  const inputObj: any = typeof File !== "undefined" && input instanceof File ? {} : (input as any) || {};
  const fileMeta: any = inputObj?.fileMeta || inputObj?.attachmentMeta || inputObj?.storedFile || inputObj?.existingFile || null;

  // 先嘗試從 caller 提供的本地 File
  let files = coerceFiles(input as any);

  // 若沒有本地檔案，但 caller 傳了遠端附件 meta（Firebase Storage downloadUrl 等），
  // 則前端直接抓回 blob 轉成 File，再走結構化解析。
  if (!files.length) {
    const fallbackMeta =
      fileMeta ||
      (Array.isArray(inputObj?.attachments) ? inputObj.attachments[0] : null) ||
      inputObj?.attachment ||
      inputObj?.fileInfo ||
      null;
    const fetched = await fetchMetaAsFile(fallbackMeta);
    if (fetched) files = [fetched];
  }

  const file = files[0];
  const file2 = files[1];
  const mode: AIAnalysisMode = (inputObj?.mode as any) || (inputObj?.purpose as any) || "analysis";
  const module = safeText(inputObj?.module || inputObj?.contextType || inputObj?.source || "");

  const contextMode: "analysis" | "contract_extraction" | "version_compare" | "batch_analysis" =
    isLikelyContractExtraction(mode)
      ? "contract_extraction"
      : files.length >= 3 || mode === "batch_analysis"
      ? "batch_analysis"
      : files.length === 2 || mode === "version_compare"
      ? "version_compare"
      : "analysis";

  const fieldsStr = pickFieldsStr(inputObj);
  const templateStr = pickTemplateStr(inputObj);
  const extractedText = safeText(inputObj?.extractedText || inputObj?.text || "");
  const extractedText2 = safeText(inputObj?.extractedText2 || "");
  const userInstruction = safeText(inputObj?.userInstruction || inputObj?.prompt || "");

  async function bestEffortExtract(file: File): Promise<string> {
    try {
      const r = await extractPlainTextFromFile(file);
      return safeText(r?.text);
    } catch {
      return "";
    }
  }

  // 1) 有本地 File：走 /api/ai-analyze-document（最穩、回結構化 JSON）
  if (file) {
    const documentType = safeText(inputObj?.documentType || inputObj?.docType || "");
    const wantsCustomAssistantJson =
      contextMode === "analysis" &&
      (documentType === "seal_application" || documentType === "brand_asset" || documentType === "ip_asset");

    if (wantsCustomAssistantJson) {
      try {
        const promptText = buildCustomJsonPrompt({
          documentType: documentType as any,
          fieldsStr,
          templateStr,
          userInstruction,
        });
        const extracted = extractedText || (await bestEffortExtract(file));
        const r = await analyzeViaAssistantWithExtractedText({
          name: file.name || safeText(fileMeta?.name) || "attachment",
          mimeType: file.type || safeText(fileMeta?.mimeType),
          extractedText: extracted,
          promptText,
        });
        if (!r.ok) return r as any;

        const parsed: any = r.parsed || {};
        const title = safeText(parsed.title || parsed.name) || file.name || "";
        const mainDate = safeText(parsed.mainDate || parsed.expiryDate || "");
        const amountText = safeText(parsed.amount);
        const fields = normalizeFieldsSchemaV1({
          title,
          mainDate,
          summary: safeText(parsed.summary),
          amount: amountText,
          currency: safeText(parsed.currency),
          counterparty: safeText(parsed.counterparty),
          category: safeText(parsed.category),
          extra: parsed,
        });

        const out: any = {
          ok: true,
          ...parsed,
          title,
          summary: safeText(parsed.summary) || "",
          description: safeText(parsed.summary) || "",
          fields,
          keyRisks: [],
          riskFlags: [],
          todoFields: [],
          suggestReply: safeText(parsed.draft || parsed.suggestReply) || "",
          draft: safeText(parsed.draft) || "",
          riskAssessmentText: safeText(parsed.riskNotes) || "",
          raw: { assistantParsed: parsed, assistantRaw: r.raw },
          meta: { api: "ai-assistant", mode: "custom_json", documentType },
        };

        return out;
      } catch (e) {
        // fall through to standard analyzer
      }
    }
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("context", contextMode);
    if (fieldsStr) form.append("fields", fieldsStr);
    if (templateStr) form.append("template", templateStr);
    if (module) form.append("module", module);
    if (userInstruction) form.append("userInstruction", userInstruction);

    // 為了 PDF/OCR 可用：
    // - 若 caller 未提供 extractedText，則前端嘗試抽字（PDF text layer + OCR fallback）
    // - 其他格式可留空，讓後端自行解析（docx/xlsx/txt）
    let e1 = extractedText;
    if (!e1 && file?.type === "application/pdf") {
      e1 = await bestEffortExtract(file);
    }
    if (e1) form.append("extractedText", e1);

    // version compare：第二份檔案
    if (contextMode === "version_compare" && file2) {
      form.append("file2", file2, file2.name);
      let e2 = extractedText2;
      if (!e2 && file2?.type === "application/pdf") {
        e2 = await bestEffortExtract(file2);
      }
      if (e2) form.append("extractedText2", e2);
    }

    // batch analysis：多檔合併（主約＋附件＋補充協議...）
    if (contextMode === "batch_analysis" && files.length >= 2) {
      const batch = [] as any[];
      for (const f of files.slice(0, 5)) {
        // 盡量抽字（支援 docx/pdf/txt/md；其他格式回空字串）
        const t = await bestEffortExtract(f);
        batch.push({ fileName: f.name, mimeType: f.type, size: (f as any).size, extractedText: t });
      }
      form.append("batch", JSON.stringify(batch));
      // backward-compat (older backend might read batchJson)
      form.append("batchJson", JSON.stringify(batch));
    }

    try {
      const res = await fetch("/api/ai-analyze-document", { method: "POST", body: form });
      const data = await parseJsonSafe(res);

      if (!res.ok) {
        const msg = safeText(data?.message) || safeText(data?.error) || `AI 分析失敗（HTTP ${res.status}）`;
        return { ok: false, message: msg, raw: data };
      }

      // ai-analyze-document：contract_extraction 會直接回傳 schema JSON；其他則回 {summary, fields, ...}
      if (contextMode === "contract_extraction") {
        const result = mapContractExtractionToResult(
          { name: file.name, type: file.type, size: (file as any).size },
          data
        );
        // 產出留痕（best-effort，不影響主流程）
        saveAiAnalysisLog({
          module,
          mode: contextMode,
          files: files.map((f) => ({ name: f.name, type: f.type, size: (f as any).size })),
          result,
        }).catch(() => void 0);
        return result;
      }

      const result = mapDefaultAnalysisToResult({ name: file.name, type: file.type, size: (file as any).size }, data);
      saveAiAnalysisLog({
        module,
        mode: contextMode,
        files: files.map((f) => ({ name: f.name, type: f.type, size: (f as any).size })),
        result,
      }).catch(() => void 0);
      return result;
    } catch (e: any) {
      return { ok: false, message: `AI 服務連線失敗：${e?.message || "unknown error"}` };
    }
  }

  // 2) 沒有本地 File，但有 downloadUrl：走 /api/ai-assistant（後端會 fetch 檔案）
  const url = safeText(inputObj?.url) || safeText(inputObj?.fileMeta?.downloadUrl) || safeText(inputObj?.fileMeta?.url);
  const name = safeText(inputObj?.fileMeta?.name) || "attachment";
  const mimeType = safeText(inputObj?.fileMeta?.mimeType) || safeText(inputObj?.fileMeta?.contentType) || safeText(inputObj?.contentType);

  if (url) {
    // 優先：嘗試把 downloadUrl 拉回成 File，在前端做解析（PDF文字層/OCR/Docx 解析等）
    const __fromUrl = Boolean((inputObj as any)?.__fromUrl);
    if (!__fromUrl && typeof File !== "undefined" && typeof fetch === "function") {
      try {
        const resp = await fetch(url);
        if (resp.ok) {
          const blob = await resp.blob();
          const f = new File([blob], name || "attachment", { type: mimeType || blob.type || "application/octet-stream" });
          return analyzeUploadedFile({
            ...(inputObj as any),
            file: f,
            files: [f],
            rawFiles: [f],
            __fromUrl: true,
            fileMeta: { ...(inputObj as any)?.fileMeta, name: name || f.name, mimeType: f.type, downloadUrl: url },
          });
        }
      } catch {
        // ignore, fallback to assistant
      }
    }

    try {
      const r = await analyzeViaAssistant({
        url,
        name,
        mimeType,
        contextMode,
        fieldsStr,
        templateStr,
        userInstruction,
      });
      if (!r.ok) return r;

      const parsed = (r as any).parsed;

      if (contextMode === "contract_extraction") {
        return mapContractExtractionToResult({ name }, parsed);
      }

      // assistant 版若直接回 schema：補一層 wrapper 以符合 mapDefaultAnalysisToResult
      const wrapper = parsed?.summary || parsed?.fields || parsed?.keyRisks ? parsed : { summary: jsonStringifySafe(parsed, 20000) };
      return mapDefaultAnalysisToResult({ name }, wrapper);
    } catch (e: any) {
      return { ok: false, message: `AI 服務連線失敗：${e?.message || "unknown error"}` };
    }
  }

  return { ok: false, message: "未取得要分析的檔案（File 或 downloadUrl）。請先選擇/上傳附件後再按 AI。" };
}

// 舊名稱相容
export async function aiAnalyzeUploadedFile(input: AnalyzeInputLike) {
  return analyzeUploadedFile(input);
}

export async function callLegalAssistant(input: CallLegalAssistantInput): Promise<CallLegalAssistantResponse> {
  const payload: any =
    typeof input === "string"
      ? { promptText: input }
      : {
          mode: input?.mode || "general",
          source: input?.contextType || "",
          promptText: safeText(input?.userInstruction) || safeText(input?.notes) || safeText((input as any)?.promptText),
          currentFormSummary: jsonStringifySafe(input?.currentFields, 5000),
          templateText: safeText(input?.templateText),
          // Allow callers to pass pre-extracted text (e.g., OCR / PDF text layer)
          extractedText: safeText((input as any)?.extractedText || (input as any)?.text || ""),
          attachments: Array.isArray(input?.attachments) ? input.attachments : [],
        };

  try {
    const res = await fetch("/api/ai-assistant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await parseJsonSafe(res);

    if (!res.ok) {
      const msg = safeText(data?.message) || safeText(data?.error) || `AI 回覆失敗（HTTP ${res.status}）`;
      return { ok: false, text: "", message: msg, raw: data };
    }

    const reply = safeText(data?.reply) || (typeof data === "string" ? data : jsonStringifySafe(data, 20000));
    return { ok: true, text: reply, raw: data };
  } catch (e: any) {
    return { ok: false, text: "", message: `AI 服務連線失敗：${e?.message || "unknown error"}` };
  }
}

// 部分模組可能用這個別名
export async function callComplianceAssistant(input: CallLegalAssistantInput) {
  return callLegalAssistant(input);
}
